import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImpersonateButton } from '@/components/ImpersonateButton';
import { usePermissions } from '@/hooks/usePermissions';
import {
  STATUS_TONE,
  STATUS_LABEL,
  TARIFF_LABEL,
  initials,
  formatDate,
  formatBalance,
} from '@/lib/clientFormat';
import type { ClientListItem } from '@/api/clients';
import { ClientRowMenu } from './ClientRowMenu';

export function ClientRow({
  client,
  onDeleted,
  col2Style,
  isDeleting,
  onDeletingChange,
}: {
  client: ClientListItem;
  onDeleted: () => void;
  col2Style: CSSProperties | undefined;
  isDeleting: boolean;
  onDeletingChange: (id: number | null) => void;
}) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const tone = STATUS_TONE[client.status] ?? 'neutral';
  const statusLabel = STATUS_LABEL[client.status] ?? client.status;
  const tariffLabel = TARIFF_LABEL[client.tariffType] ?? client.tariffType;
  // Список даёт только .list; карточку открывает .view/.add/.edit (см. маршруты
  // customers/{id} в Laravel). Без них строка остаётся некликабельной.
  const canOpen = can(
    'platform*.customers.view',
    'platform*.customers.add',
    'platform*.customers.edit',
  );

  return (
    <tr
      className={canOpen ? 'table__row--clickable' : undefined}
      onClick={canOpen ? () => navigate(`/clients/${client.id}`) : undefined}
    >
      <td className="sticky-col sticky-col--1">
        <div className="company-cell">
          <span className="company-mark">{initials(client.company)}</span>
          <span className="cell-primary">{client.company ?? 'Без названия'}</span>
        </div>
      </td>
      <td className="sticky-col sticky-col--2" style={col2Style}>
        <div className="cell-primary">{client.adminContact.name ?? '–'}</div>
        {client.adminContact.email && (
          <div className="cell-sub">
            <a href={`mailto:${client.adminContact.email}`} onClick={(e) => e.stopPropagation()}>
              {client.adminContact.email}
            </a>
          </div>
        )}
      </td>
      <td>
        <span className={`badge badge--${tone}`}>
          <span className="badge__dot" />
          {statusLabel}
        </span>
      </td>
      <td className="cell-center" onClick={(e) => e.stopPropagation()}>
        <ImpersonateButton accountId={client.id} service="studio" disabled={!client.canImpersonate} />
      </td>
      <td className="cell-center" onClick={(e) => e.stopPropagation()}>
        <ImpersonateButton accountId={client.id} service="maintest5i" disabled={!client.canImpersonate} />
      </td>
      <td className="cell-muted">{tariffLabel}</td>
      <td className="tabular">
        {client.tariffType === 'license' ? (
          '–'
        ) : (
          <>
            <div className="cell-primary">{formatBalance(client.balance)}</div>
            <div className="cell-sub">Доступно: {formatBalance(client.availableBalance)}</div>
          </>
        )}
      </td>
      <td className="tabular">{client.testsCount}</td>
      <td className="cell-muted">
        {client.managers.length ? client.managers.map((m) => m.name).join(', ') : '–'}
      </td>
      <td className="cell-muted tabular">{formatDate(client.lastActivityAt)}</td>
      <td className="cell-muted tabular">{formatDate(client.connectedAt)}</td>
      <td className="cell-muted tabular">{formatDate(client.validity)}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <ClientRowMenu
          clientId={client.id}
          clientName={client.company}
          onDeleted={onDeleted}
          isDeleting={isDeleting}
          onDeletingChange={onDeletingChange}
        />
      </td>
    </tr>
  );
}
